import { Injectable } from '@angular/core';
import { ApiBaseService } from '@core/services/api-base.service';
import { Mapper } from '@core/services/mapper';
import { CustomerFleetType } from '@shared-features/enums/customer-fleet-type.enum';
import { DateRangeType } from '@shared/enums/date-range-type.enum';
import { FleetStatus } from '@shared-features/enums/fleet-status.enum';
import { Status } from '@shared/enums/status.enum';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiConstant } from '@shared/constants/api.constant';
import { BusinessType } from '@shared-features/enums/business-type.enum';
import { filterNullEntity } from '@shared/utils/filter-null-entity.util';
import { Fleet } from '@features/fleets/models/fleet';
import { FleetsDetails } from '@features/fleets/models/fleets-details';
import { SortDirection } from '@shared/enums/sort-direction.enum';
import { FleetsClassificationCounts } from '@features/fleets/models/fleets-classification-counts.model';
import { FleetsTrucksDriversCounts } from '@features/fleets/models/fleets-trucks-drivers-counts.model';
import { GlobalAndSpecificFleetsAndContractsCounts } from '@features/fleets/models/global-and-specific-fleets-and-contracts-counts.model';
import { DateUnitType } from '@shared/enums';
import { TopTenFleetsResponse } from '@features/fleets/models/top-ten-fleets-response.model';
import { FleetClassification } from '@shared-features/enums/fleet-classification.enum';

@Injectable({
  providedIn: 'root',
})
export class FleetsCommonApiService {
  constructor(private baseAPI: ApiBaseService, private mapper: Mapper) {}

  getAllFleets(params: {
    'status[]'?: FleetStatus[] | Status[];
    'type[]'?: CustomerFleetType;
    customerId?: number;
    searchTerm?: string;
    offset?: number;
    limit?: number;
    pageSize?: number;
    activationDate?: DateRangeType;
    isForExport?: boolean;
    fleetClassification?: FleetClassification;
    sortField?: string;
    sortDirection?: SortDirection;
    dateRangeType?: DateRangeType;
    businessType?: BusinessType;
  }): Observable<FleetsDetails> {
    return this.baseAPI
      .get(ApiConstant.GET_ALL_FLEETS, { params: filterNullEntity<typeof params>(params) })
      .pipe(
        map((res) => {
          return this.mapper.fromJson(FleetsDetails, res.data);
        })
      );
  }

  getTopTenFleets(params: {
    enableCache: boolean;
    fleetType?: CustomerFleetType;
    fleetClassification?: FleetClassification;
    dateUnitType?: DateUnitType;
    selectedDate?: string;
  }): Observable<TopTenFleetsResponse> {
    return this.baseAPI
      .post(ApiConstant.GET_TOP_TEN_FLEETS, { ...filterNullEntity<typeof params>(params) })
      .pipe(
        map((res) => {
          return this.mapper.fromJson(TopTenFleetsResponse, res.data);
        })
      );
  }

  getFleetsCanPickNewShipments(customerId: number): Observable<Fleet[]> {
    return this.baseAPI
      .get(ApiConstant.GET_FLEETS_CAN_PICK_NEW_SHIPMENTS + `/${customerId}`)
      .pipe(map((res) => this.mapper.fromJson(Fleet, res.data)));
  }

  getFleetsClassificationCount(enableCache: boolean): Observable<FleetsClassificationCounts> {
    return this.baseAPI
      .post(ApiConstant.GET_FLEETS_CLASSIFICATION_COUNTS, { enableCache })
      .pipe(map((res) => this.mapper.fromJson(FleetsClassificationCounts, res.data)));
  }

  getFleetsTrucksDriversCounts(enableCache: boolean): Observable<FleetsTrucksDriversCounts> {
    return this.baseAPI
      .post(ApiConstant.GET_FLEETS_TRUCKS_DRIVERS_COUNTS, { enableCache })
      .pipe(map((res) => this.mapper.fromJson(FleetsTrucksDriversCounts, res.data)));
  }

  getGlobalAndSpecificFleetsAndContractsCounts(
    enableCache: boolean
  ): Observable<GlobalAndSpecificFleetsAndContractsCounts> {
    return this.baseAPI
      .post(ApiConstant.GET_GLOBAL_AND_SPECIFIC_FLEETS_AND_CONTRACTS_COUNTS, { enableCache })
      .pipe(
        map((res) =>
          this.mapper.fromJson(GlobalAndSpecificFleetsAndContractsCounts, res.data?.list)
        )
      );
  }
}
